/**
 * React Query hooks for establishment list and by-id fetch.
 */

import { useQuery, useQueries } from '@tanstack/react-query';
import { getEstablishments, getEstablishment } from '@/services/establishments.service';
import type { Establishment } from '@/types/establishment.types';

/** `GET /establishments` — full catalogue. */
export const useEstablishments = () => {
  return useQuery({
    queryKey: ['establishments'],
    queryFn: () => getEstablishments(),
  });
};

/** `GET /establishments/:id` */
export const useEstablishment = (id: string) => {
  return useQuery({
    queryKey: ['establishments', id],
    queryFn: () => getEstablishment(id),
    enabled: !!id,
  });
};

/** Batch-fetches multiple establishments by ID. Returns a map id→establishment and a combined isLoading flag. */
export const useEstablishmentsByIds = (ids: string[]) => {
  const results = useQueries({
    queries: ids.map((id) => ({
      queryKey: ['establishments', id],
      queryFn: () => getEstablishment(id),
      enabled: !!id,
    })),
  });
  const isLoading = ids.length > 0 && results.some((r) => r.isLoading);
  const establishments = Object.fromEntries(
    ids.map((id, i) => [id, results[i]?.data as Establishment | undefined]),
  );
  return { isLoading, establishments };
};
